import React, { useMemo, useRef, useState } from "react";

import type { EndorsementResponse } from "@tm/insurance/api";
import { submitEndorsement } from "@tm/insurance/api";
import {
  FormattedText,
  ImageComponent,
  LandingHeaderMobile,
  ReactPortalComp,
  Spinner,
} from "@tm/static";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";
import CommonModalDweb from "../../../../components/CommonModalDweb/CommonModalDweb";
import { ORDER_DETAIL_V2_ASSETS } from "../../constants";
import type {
  EditTravellersModalProps,
  EndorsementView,
  Entity,
} from "../../types";
import {
  convertYYYYMMDDToDDMMYYYY,
  FormCard,
  FormRendererFields,
  normalizeFormConfig,
  useFormRenderer,
} from "../FormRenderer";
import { InfoModalContent } from "../InfoModalContent";
import {
  EditTravellersSheetStyle,
  ModalFooter,
  ModalFooterActions,
  SaveButton,
  SheetContent,
  StickyErrorBanner,
  StickyErrorIcon,
  StickyErrorText,
  SubmitModalWrapper,
  TravellerFormSection,
} from "./EditTravellersModal.style";

// ── Entity data extractor for traveller form ────────────────────────────

const extractTravellerData = (entity: Entity): Record<string, unknown> =>
  entity.data;

// ── Component ───────────────────────────────────────────────────────────

export const EditTravellersModal: React.FC<EditTravellersModalProps> = ({
  isOpen,
  onClose,
  formConfig: rawFormConfig,
  entities,
  bookingId,
  onSave,
  onEndorsementSuccess,
  modalTitle = "Edit Travellers",
  saveButtonLabel = "SAVE DETAILS",
}) => {
  const device = useBreakpointV2();

  // Normalize fields that may arrive as either arrays or maps from the API
  const formConfig = useMemo(
    () => normalizeFormConfig(rawFormConfig),
    [rawFormConfig]
  );

  // ── Shared form state ─────────────────────────────────────────────────

  const {
    formData,
    errors,
    initialDataRef,
    getFieldRef,
    handleMaskedFocus,
    handleMaskedBlur,
    handleFieldChange,
    handleDynamicFieldChange,
    toggleDynamicField,
    validateAllFields,
    hasFormDataChanged,
    setErrors,
  } = useFormRenderer({
    isOpen,
    formConfig,
    entities,
    extractEntityData: extractTravellerData,
  });

  // ── Endorsement state (modal-specific) ────────────────────────────────

  const [isLoading, setIsLoading] = useState(false);
  const [endorsementView, setEndorsementView] =
    useState<EndorsementView>("form");
  const [endorsementData, setEndorsementData] =
    useState<EndorsementResponse | null>(null);
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const pendingRefreshRef = useRef(false);

  // Reset endorsement state when modal opens
  const prevIsOpenRef = useRef(false);
  React.useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setEndorsementView("form");
      setEndorsementData(null);
      setApiErrorMessage(null);
      setIsLoading(false);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  // Clear API error when user edits a field
  const wrappedHandleFieldChange = (
    entityIndex: number,
    fieldKey: string,
    value: string
  ) => {
    if (apiErrorMessage) setApiErrorMessage(null);
    handleFieldChange(entityIndex, fieldKey, value);
  };

  const wrappedHandleDynamicFieldChange = (
    entityIndex: number,
    triggerId: string,
    fieldKey: string,
    value: string
  ) => {
    if (apiErrorMessage) setApiErrorMessage(null);
    handleDynamicFieldChange(entityIndex, triggerId, fieldKey, value);
  };

  // ── Save / Close ──────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!validateAllFields()) return;

    setIsLoading(true);
    try {
      const updatedEntities: Entity[] = entities.map((entity, index) => {
        const entityData = formData[index];
        const updatedData: Record<string, any> = { ...entity.data };

        formConfig.fields.forEach((field) => {
          const fieldKey = field.name;
          let value = String(entityData[fieldKey] || "");
          if (field.type === "DATE" && value) {
            value = convertYYYYMMDDToDDMMYYYY(value);
          }
          updatedData[fieldKey] = value;
        });

        formConfig.dynamicFields?.forEach((df) => {
          const dynamicData = entityData.dynamicFields?.[df.triggerId];
          if (dynamicData?.visible) {
            df.fields.forEach((field) => {
              updatedData[field.name] = dynamicData[field.name] || "";
            });
          }
        });

        return { ...entity, data: updatedData };
      });

      const payload = updatedEntities.map((e) => ({
        entityId: e.id,
        ...e.data,
      }));
      const res = await submitEndorsement({ bookingId, payload });
      setEndorsementData(res);
      const status = (res?.status ?? "").toUpperCase();

      if (status === "SUCCESS") {
        setEndorsementView("success");
        setApiErrorMessage(null);
        initialDataRef.current = JSON.parse(JSON.stringify(formData));
        await Promise.resolve(onSave(updatedEntities));
        pendingRefreshRef.current = true;
      } else if (status === "ERROR" || status === "VALIDATION_ERROR") {
        setApiErrorMessage(
          res?.desc ||
            res?.subtitle ||
            res?.title ||
            "An error occurred. Please try again."
        );
        setEndorsementView("form");
      } else {
        setEndorsementView("generating");
      }
    } catch {
      setApiErrorMessage("An error occurred. Please try again.");
      setEndorsementView("form");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (pendingRefreshRef.current) {
      pendingRefreshRef.current = false;
      onEndorsementSuccess?.();
    }
    onClose();
  };

  // ── Render ────────────────────────────────────────────────────────────

  if (!isOpen) return null;

  const isResultView =
    endorsementView === "success" || endorsementView === "generating";
  const formChanged = hasFormDataChanged();

  const renderFormContent = () => (
    <>
      <TravellerFormSection>
        {formData.map((entityData, entityIndex) => (
          <FormCard key={entityIndex}>
            <FormRendererFields
              entityIndex={entityIndex}
              entityData={entityData}
              entityErrors={errors[entityIndex] || {}}
              formConfig={formConfig}
              getFieldRef={getFieldRef}
              handleFieldChange={wrappedHandleFieldChange}
              handleMaskedFocus={handleMaskedFocus}
              handleMaskedBlur={handleMaskedBlur}
              handleDynamicFieldChange={wrappedHandleDynamicFieldChange}
              toggleDynamicField={toggleDynamicField}
              setErrors={setErrors}
            />
          </FormCard>
        ))}
      </TravellerFormSection>

      <ModalFooter>
        {apiErrorMessage && (
          <StickyErrorBanner>
            <StickyErrorIcon>
              <ImageComponent
                src={ORDER_DETAIL_V2_ASSETS.VALIDATION_ERROR_ICON}
                width="24"
                height="24"
                alt="Error"
              />
            </StickyErrorIcon>
            <StickyErrorText>
              <FormattedText value={{ html: apiErrorMessage }} />
            </StickyErrorText>
          </StickyErrorBanner>
        )}
        <ModalFooterActions>
          <SaveButton
            onClick={handleSave}
            hasChanges={formChanged}
            isLoading={isLoading}
            disabled={!formChanged || isLoading}
          >
            {isLoading ? (
              <>
                <Spinner
                  spinnerWidth={16}
                  spinnerColor={theme.whiteText}
                  thickness={2}
                />
                <FormattedText value={saveButtonLabel} />
              </>
            ) : (
              <FormattedText value={saveButtonLabel} />
            )}
          </SaveButton>
        </ModalFooterActions>
      </ModalFooter>
    </>
  );

  const renderResultView = () => (
    <SubmitModalWrapper>
      <InfoModalContent
        viewType={endorsementView}
        data={endorsementData}
        renderAction={(data) => {
          if (
            endorsementView === "success" &&
            data.cta?.isEnabled !== false &&
            data.cta?.label &&
            data.newPolicyUrl
          ) {
            return (
              <SaveButton
                as="a"
                href={data.newPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
                hasChanges={true}
                isLoading={false}
                style={{ marginTop: "16px", textDecoration: "none" }}
              >
                <FormattedText value={data.cta.label} />
              </SaveButton>
            );
          }
          return null;
        }}
      />
    </SubmitModalWrapper>
  );

  // Mobile: full-page sheet
  if (device === "sm") {
    return (
      <ReactPortalComp>
        <EditTravellersSheetStyle>
          <LandingHeaderMobile
            displayWhiteHeader
            headerTitle={isResultView ? "" : modalTitle}
            handleBackBtn={handleClose}
          />
          <SheetContent>
            {isResultView ? renderResultView() : renderFormContent()}
          </SheetContent>
        </EditTravellersSheetStyle>
      </ReactPortalComp>
    );
  }

  // Desktop: modal
  return (
    <CommonModalDweb
      customTitle={isResultView ? "" : modalTitle}
      handleClick={handleClose}
      customWidth={isResultView ? "420px" : "80%"}
      customHeight={isResultView ? "auto" : "640px"}
    >
      {isResultView ? renderResultView() : renderFormContent()}
    </CommonModalDweb>
  );
};
